import React from 'react';
import ColorField from '../../fields/ColorField';
import { useSavedColors } from '../../../context/SavedColorsContext';

/**
 * SettingsTab - Page-level settings
 *
 * Includes:
 * - Background color picker with gradient support and saved colors
 * - Section layout (Boxed, Full Width, Custom)
 * - Custom width settings
 */
export default function SettingsTab({ pageConfig, updatePageConfig }) {
  // Get saved colors from context
  const { savedColors, onSaveColor, onDeleteSavedColor } = useSavedColors();

  // Handle background color change
  const handleBackgroundColorChange = (newValue) => {
    updatePageConfig({ page_background_color: newValue });
  };

  // Handle save color - receives value and label from ColorPicker
  const handleSaveColor = (value, label) => {
    onSaveColor(value, label);
  };

  return (
    <div className="p-4 space-y-4">
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
        Page Settings
      </h3>

      {/* Background Color */}
      <div>
        <ColorField
          field={{
            key: 'page_background_color',
            label: 'Background Color',
            default: '#f9fafb',
            allowTransparent: true,
            allowGradient: true,
          }}
          value={pageConfig.page_background_color || '#f9fafb'}
          onChange={handleBackgroundColorChange}
          savedColors={savedColors}
          onSaveColor={handleSaveColor}
          onDeleteSavedColor={onDeleteSavedColor}
        />
      </div>

      {/* Section Layout */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Section Layout
        </label>
        <select
          value={pageConfig.section_width || 'boxed'}
          onChange={(e) => updatePageConfig({ section_width: e.target.value })}
          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="boxed">Boxed (centered container)</option>
          <option value="full-width">Full Width</option>
          <option value="custom">Custom Width</option>
        </select>
      </div>

      {/* Custom Width Settings */}
      {pageConfig.section_width === 'custom' && (
        <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-100">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Section Width
            </label>
            <input
              type="text"
              value={pageConfig.custom_section_width || '800px'}
              onChange={(e) => updatePageConfig({ custom_section_width: e.target.value })}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="800px or 80%"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Max Width
            </label>
            <input
              type="text"
              value={pageConfig.section_max_width || '1200px'}
              onChange={(e) => updatePageConfig({ section_max_width: e.target.value })}
              className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="1200px"
            />
          </div>
        </div>
      )}
    </div>
  );
}
